import { useEffect, useState, useCallback, useRef } from "react";
import { useSearchParams } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  Brain,
  Plus,
  Trash,
  Lightbulb,
  MagnifyingGlass,
  Tag,
  PencilSimple,
  X,
} from "@phosphor-icons/react";
import * as API from "../lib/api";
import { Button } from "../components/ui/button";
import { Textarea } from "../components/ui/textarea";
import { Input } from "../components/ui/input";
import ItemMenu from "../components/ItemMenu";
import { toast } from "sonner";

export default function Memory() {
  const [memories, setMemories] = useState([]);
  const [allTags, setAllTags] = useState([]);
  const [text, setText] = useState("");
  const [tagInput, setTagInput] = useState("");
  const [tagDraft, setTagDraft] = useState([]);
  const [loading, setLoading] = useState(false);

  const [search, setSearch] = useState("");
  const [activeTag, setActiveTag] = useState(null);

  const [editId, setEditId] = useState(null);
  const [editContent, setEditContent] = useState("");
  const [editTags, setEditTags] = useState([]);

  const [searchParams, setSearchParams] = useSearchParams();
  const highlightId = searchParams.get("highlight");
  const highlightRef = useRef(null);
  const [highlightActive, setHighlightActive] = useState(null);

  const load = useCallback(async () => {
    try {
      const params = {};
      if (search.trim()) params.q = search.trim();
      if (activeTag) params.tag = activeTag;
      const [list, tags] = await Promise.all([
        API.listMemory(params),
        API.listMemoryTags(),
      ]);
      setMemories(list);
      setAllTags(tags);
    } catch {
      toast.error("Failed to load memories");
    }
  }, [search, activeTag]);

  useEffect(() => {
    const id = setTimeout(load, 200); // debounce search
    return () => clearTimeout(id);
  }, [load]);

  // Highlight + scroll to memory passed via ?highlight=ID
  useEffect(() => {
    if (!highlightId || memories.length === 0) return;
    const target = memories.find((m) => m.id === highlightId);
    if (!target) return;
    setHighlightActive(highlightId);
    setTimeout(() => {
      highlightRef.current?.scrollIntoView({
        behavior: "smooth",
        block: "center",
      });
    }, 80);
    const tid = setTimeout(() => {
      setHighlightActive(null);
      setSearchParams((prev) => {
        const next = new URLSearchParams(prev);
        next.delete("highlight");
        return next;
      }, { replace: true });
    }, 2800);
    return () => clearTimeout(tid);
  }, [highlightId, memories, setSearchParams]);

  const draftAddTag = () => {
    const t = tagInput.trim().toLowerCase();
    if (!t || tagDraft.includes(t)) {
      setTagInput("");
      return;
    }
    setTagDraft((prev) => [...prev, t]);
    setTagInput("");
  };

  const draftRemoveTag = (t) =>
    setTagDraft((prev) => prev.filter((x) => x !== t));

  const add = async () => {
    if (!text.trim()) return;
    setLoading(true);
    try {
      await API.createMemory(text.trim(), tagDraft);
      setText("");
      setTagDraft([]);
      setTagInput("");
      await load();
      toast.success("Memory added");
    } catch {
      toast.error("Failed to add memory");
    } finally {
      setLoading(false);
    }
  };

  const remove = async (id) => {
    try {
      await API.deleteMemory(id);
      setMemories((m) => m.filter((x) => x.id !== id));
      // refresh tags list
      API.listMemoryTags().then(setAllTags).catch(() => {});
      toast.success("Memory deleted");
    } catch {
      toast.error("Failed to delete");
    }
  };

  const startEdit = (m) => {
    setEditId(m.id);
    setEditContent(m.content);
    setEditTags(m.tags || []);
  };

  const cancelEdit = () => {
    setEditId(null);
    setEditContent("");
    setEditTags([]);
  };

  const saveEdit = async () => {
    if (!editContent.trim()) return;
    try {
      await API.updateMemory(editId, {
        content: editContent.trim(),
        tags: editTags,
      });
      cancelEdit();
      await load();
      toast.success("Memory updated");
    } catch {
      toast.error("Failed to update");
    }
  };

  const filteredCount = memories.length;

  return (
    <div className="overflow-y-auto scroll-contain" data-testid="memory-page">
      <div className="max-w-3xl mx-auto px-4 sm:px-6 py-8 sm:py-10">
        <div className="flex items-start gap-4 mb-7">
          <div className="w-12 h-12 rounded-2xl bg-cyan-500/10 border border-cyan-500/30 flex items-center justify-center shrink-0">
            <Brain size={22} weight="duotone" className="text-cyan-400" />
          </div>
          <div>
            <h1 className="font-display text-2xl sm:text-3xl font-semibold tracking-tight">
              Shared memory
            </h1>
            <p className="text-white/60 text-sm mt-1">
              Facts Journey remembers across every chat. Tag, search, and curate.
            </p>
          </div>
        </div>

        {/* Add */}
        <div className="rounded-2xl bg-[#0f0f0f] border border-white/5 p-4 mb-6">
          <Textarea
            value={text}
            onChange={(e) => setText(e.target.value)}
            placeholder="e.g., I'm a software engineer based in Toronto. I prefer concise replies."
            className="bg-transparent border-white/10 text-[15px] resize-none min-h-[80px] focus-visible:ring-cyan-500/40"
            data-testid="memory-input"
          />
          <div className="flex flex-wrap gap-2 mt-3 items-center">
            <div className="flex gap-2 flex-1 min-w-[200px]">
              <Input
                value={tagInput}
                onChange={(e) => setTagInput(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === ",") {
                    e.preventDefault();
                    draftAddTag();
                  }
                }}
                placeholder="Add tag…"
                className="bg-transparent border-white/10 focus-visible:ring-cyan-500/40 h-9"
                data-testid="memory-tag-input"
              />
              <Button
                onClick={draftAddTag}
                variant="outline"
                size="sm"
                className="border-white/10 hover:bg-white/5"
                data-testid="memory-tag-add"
              >
                Tag
              </Button>
            </div>
            <Button
              onClick={add}
              disabled={loading || !text.trim()}
              className="bg-cyan-500 hover:bg-cyan-400 text-black font-medium rounded-xl"
              data-testid="memory-add-button"
            >
              <Plus size={16} weight="bold" className="mr-1" />
              Add memory
            </Button>
          </div>
          {tagDraft.length > 0 && (
            <div className="flex flex-wrap gap-1.5 mt-3">
              {tagDraft.map((t) => (
                <TagChip key={t} label={t} onRemove={() => draftRemoveTag(t)} />
              ))}
            </div>
          )}
        </div>

        {/* Search + tag filters */}
        <div className="space-y-3 mb-5">
          <div className="relative">
            <MagnifyingGlass
              size={16}
              className="absolute left-3 top-1/2 -translate-y-1/2 text-white/40"
            />
            <Input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search memories…"
              className="bg-[#0f0f0f] border-white/5 pl-9 pr-9 focus-visible:ring-cyan-500/40 h-10"
              data-testid="memory-search"
            />
            {search && (
              <button
                onClick={() => setSearch("")}
                className="absolute right-1 top-1/2 -translate-y-1/2 text-white/40 hover:text-white p-2"
                aria-label="Clear search"
                data-testid="memory-search-clear"
              >
                <X size={14} />
              </button>
            )}
          </div>

          {allTags.length > 0 && (
            <div className="flex gap-1.5 flex-wrap">
              <FilterChip
                active={activeTag === null}
                onClick={() => setActiveTag(null)}
                testId="memory-filter-all"
              >
                All
              </FilterChip>
              {allTags.map((t) => (
                <FilterChip
                  key={t}
                  active={activeTag === t}
                  onClick={() => setActiveTag((x) => (x === t ? null : t))}
                  testId={`memory-filter-tag-${t}`}
                >
                  <Tag size={11} weight="duotone" className="mr-1" />
                  {t}
                </FilterChip>
              ))}
            </div>
          )}
        </div>

        {filteredCount === 0 ? (
          <div className="text-center py-16">
            <Lightbulb size={32} weight="duotone" className="text-cyan-400/60 mx-auto mb-3" />
            <p className="text-white/50 text-sm">
              {search || activeTag
                ? "No memories match your filter."
                : "No memories yet. Add one above or save a chat message as memory."}
            </p>
          </div>
        ) : (
          <ul className="space-y-2.5">
            <AnimatePresence>
              {memories.map((m) => (
                <motion.li
                  key={m.id}
                  ref={m.id === highlightActive ? highlightRef : null}
                  initial={{ opacity: 0, y: 6 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, x: -10 }}
                  className={`rounded-xl bg-[#0f0f0f] border border-white/5 px-4 py-3.5 ${
                    m.id === highlightActive ? "memory-highlight" : ""
                  }`}
                  data-testid={`memory-item-${m.id}`}
                >
                  {editId === m.id ? (
                    <EditMemoryForm
                      content={editContent}
                      setContent={setEditContent}
                      tags={editTags}
                      setTags={setEditTags}
                      onCancel={cancelEdit}
                      onSave={saveEdit}
                    />
                  ) : (
                    <div className="flex items-start gap-3">
                      <Lightbulb
                        size={16}
                        weight="duotone"
                        className="text-cyan-400/80 mt-0.5 shrink-0"
                      />
                      <div className="flex-1 min-w-0">
                        <p className="text-[15px] text-white/85 leading-relaxed break-words">
                          {m.content}
                        </p>
                        {(m.tags?.length || m.source === "chat") && (
                          <div className="flex flex-wrap gap-1.5 mt-2">
                            {m.source === "chat" && (
                              <span
                                className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-white/5 border border-white/10 text-[10px] text-white/55"
                                title="Saved from chat"
                              >
                                from chat
                              </span>
                            )}
                            {m.tags?.map((t) => (
                              <button
                                key={t}
                                onClick={() => setActiveTag(t)}
                                className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-cyan-500/10 border border-cyan-500/25 text-[10px] text-cyan-300 hover:bg-cyan-500/15"
                                data-testid={`memory-${m.id}-tag-${t}`}
                              >
                                <Tag size={9} weight="duotone" />
                                {t}
                              </button>
                            ))}
                          </div>
                        )}
                      </div>
                      <ItemMenu
                        testId={`memory-menu-${m.id}`}
                        items={[
                          {
                            label: "Edit",
                            icon: PencilSimple,
                            onClick: () => startEdit(m),
                            testId: `memory-edit-${m.id}`,
                          },
                          { type: "separator" },
                          {
                            label: "Delete",
                            icon: Trash,
                            onClick: () => remove(m.id),
                            destructive: true,
                            testId: `memory-delete-${m.id}`,
                          },
                        ]}
                      />
                    </div>
                  )}
                </motion.li>
              ))}
            </AnimatePresence>
          </ul>
        )}
      </div>
    </div>
  );
}

function TagChip({ label, onRemove }) {
  return (
    <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-cyan-500/10 border border-cyan-500/25 text-xs text-cyan-300">
      {label}
      {onRemove && (
        <button
          onClick={onRemove}
          className="text-cyan-300/70 hover:text-cyan-100"
          aria-label={`Remove ${label}`}
        >
          <X size={10} weight="bold" />
        </button>
      )}
    </span>
  );
}

function FilterChip({ children, active, onClick, testId }) {
  return (
    <button
      onClick={onClick}
      data-testid={testId}
      className={`inline-flex items-center px-3 py-1 rounded-full text-xs border transition-colors ${
        active
          ? "bg-cyan-500/15 border-cyan-500/40 text-cyan-200"
          : "bg-transparent border-white/10 text-white/60 hover:bg-white/5"
      }`}
    >
      {children}
    </button>
  );
}

function EditMemoryForm({ content, setContent, tags, setTags, onCancel, onSave }) {
  const [tagInput, setTagInput] = useState("");
  const addT = () => {
    const t = tagInput.trim().toLowerCase();
    if (!t || tags.includes(t)) {
      setTagInput("");
      return;
    }
    setTags([...tags, t]);
    setTagInput("");
  };
  const removeT = (t) => setTags(tags.filter((x) => x !== t));
  return (
    <div className="space-y-2">
      <Textarea
        value={content}
        onChange={(e) => setContent(e.target.value)}
        className="bg-transparent border-white/10 focus-visible:ring-cyan-500/40 min-h-[70px]"
        data-testid="memory-edit-textarea"
      />
      <div className="flex gap-2">
        <Input
          value={tagInput}
          onChange={(e) => setTagInput(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter" || e.key === ",") {
              e.preventDefault();
              addT();
            }
          }}
          placeholder="Add tag"
          className="bg-transparent border-white/10 h-9"
        />
        <Button
          variant="outline"
          size="sm"
          onClick={addT}
          className="border-white/10 hover:bg-white/5"
        >
          Tag
        </Button>
      </div>
      {tags.length > 0 && (
        <div className="flex flex-wrap gap-1.5">
          {tags.map((t) => (
            <TagChip key={t} label={t} onRemove={() => removeT(t)} />
          ))}
        </div>
      )}
      <div className="flex justify-end gap-2 pt-1">
        <Button variant="ghost" onClick={onCancel} className="text-white/70">
          Cancel
        </Button>
        <Button
          onClick={onSave}
          className="bg-cyan-500 hover:bg-cyan-400 text-black font-medium rounded-xl"
          data-testid="memory-edit-save"
        >
          Save
        </Button>
      </div>
    </div>
  );
}
